
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { t } from 'i18next';

import { styled, Box, Divider } from '@mui/material';

import {
  Label,
  Button,
  KeyIcon,
  Typography,
  FormEmailField,
  FormTextField,
  FormPasswordField,
  Link,
} from '@datanomik/commons-ui';

import {
  ArrowLeftSLine,
  Datanomik,
} from '@datanomik/commons-ui/icons';

import { FormProvider } from 'react-hook-form';

import { clearHeaders } from '@/interceptors/Api';
import Grid from '../../common/Grid';
import Sticky from '../../common/Sticky';
import { SwiperRaw, SwiperSlide } from '../../common/Swiper';
import { swiperStyles } from '../../common/Swiper/styles';
import { DatanomikIsoBlue } from '../../common/Logo';

import ForgotPassword from '../ForgotPassword';
import ContentBox from './ContentBox';

import useLogIn from './hooks';
import styles from './styles';

const Swiper = styled(SwiperRaw)(swiperStyles);

const LogInRaw = ({ className }) => {
  const {
    swiperRef,
    formMethodsOpenId,
    formMethodsDatanomik,
    openForgotPassword,
    handleOnSubmitOpenId,
    handleOnSubmitDatanomik,
    handleOpenForgotPassword,
    handleCloseForgotPassword,
  } = useLogIn();

  const year = (new Date()).getFullYear();

  // After logout, axios Instance was keeping headers, including Workspace ID
  // which was causing issues when login in with an user from another workspace.
  useEffect(() => {
    clearHeaders();
  }, []);

  return (
    <Box className={`${className}`}>
      <Box className="dn-container">
        <Grid container>
          <Grid className="dn-show-mobile" item xs={12}>
            <DatanomikIsoBlue className="dn-logo-mobile dn-show-mobile" />
          </Grid>
          <Grid className="dn-hidden-mobile" item xs={12} md={6} lg="auto">
            <Sticky position="top" rootSelector="body">
              <ContentBox />
            </Sticky>
          </Grid>
          <Grid item xs={12} md className="dn-flex-center">
            <Box width="100%" maxWidth="374px" m="auto 0">
              <Grid container>
                <Grid item xs={12} textAlign="center">
                  <KeyIcon size="xlarge" color="neutral">
                    <Datanomik />
                  </KeyIcon>
                </Grid>
                <Grid item xs={12} textAlign="center" mb="14px">
                  <Typography variant="h5">
                    {t('Log in to your account')}
                  </Typography>
                  <Typography variant="p3" color="text.500" mt="4px">
                    {t('Enter your details to login.')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Swiper
                    ReactSwiperProps={{
                      onSwiper: (swiper) => {
                        swiperRef.current = swiper;
                      },
                      initialSlide: 0,
                      allowTouchMove: false,
                      draggable: false,
                      autoHeight: true,
                      spaceBetween: 12,
                      grabCursor: false,
                      navigation: false,
                      keyboard: {
                        enabled: false,
                      },
                      pagination: {
                        dynamicBullets: false,
                      },
                    }}
                  >
                    <SwiperSlide key="default">
                      <Box className="dn-swiper-content">
                        <FormProvider {...formMethodsDatanomik}>
                          <form
                            id="login-form"
                            data-form-type="login"
                            autoComplete="on"
                            onSubmit={formMethodsDatanomik.handleSubmit(handleOnSubmitDatanomik)}
                            noValidate
                          >
                            <Grid container>
                              <Grid item xs={12}>
                                <FormEmailField
                                  name="username"
                                  control={formMethodsDatanomik.control}
                                  label={<Label text={t('EMAIL')} />}
                                  labelText={t('EMAIL')}
                                  autoFocus
                                  required={!(openForgotPassword)}
                                  localeText={{
                                    isRequired: t('IS REQUIRED'),
                                    validEmailAddredd: t('Please enter a valid email address'),
                                  }}
                                  slotProps={{
                                    htmlInput: {
                                      id: 'username',
                                      autoComplete: 'username',
                                      'data-lpignore': 'false',
                                      'data-1p-ignore': 'false',
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <FormPasswordField
                                  name="password"
                                  control={formMethodsDatanomik.control}
                                  label={<Label text={t('Password')} />}
                                  labelText={t('Password')}
                                  required={!(openForgotPassword)}
                                  localeText={{
                                    isRequired: t('IS REQUIRED'),
                                  }}
                                  slotProps={{
                                    htmlInput: {
                                      id: 'password',
                                      autoComplete: 'current-password',
                                      'data-lpignore': 'false',
                                      'data-1p-ignore': 'false',
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Grid container>
                                  <Grid item xs={12} textAlign="right">
                                    <Link onClick={handleOpenForgotPassword}>
                                      {t('Forgot your password?')}
                                    </Link>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Button
                                      fullWidth
                                      type="submit"
                                      variant="contained"
                                    >
                                      {t('Login')}
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Divider className="dn-divider-buttons" textAlign="center">
                                      <Typography>{t('Or')}</Typography>
                                    </Divider>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Button
                                      fullWidth
                                      type="button"
                                      variant="outlined"
                                      color="black"
                                      onClick={() => {
                                        swiperRef.current?.slideTo(1, 500);
                                      }}
                                    >
                                      {t('Login to an external account')}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </form>
                        </FormProvider>
                      </Box>
                    </SwiperSlide>
                    <SwiperSlide key="external">
                      <Box className="dn-swiper-content">
                        <FormProvider {...formMethodsOpenId}>
                          <form
                            onSubmit={formMethodsOpenId.handleSubmit(handleOnSubmitOpenId)}
                            noValidate
                          >
                            <Grid container>
                              <Grid item xs={12}>
                                <Grid container>
                                  <Grid item xs>
                                    <FormTextField
                                      name="loginDomain"
                                      control={formMethodsOpenId.control}
                                      labelText={t('Company name')}
                                      placeholder={t('Company name')}
                                      required
                                      localeText={{
                                        isRequired: t('IS REQUIRED'),
                                      }}
                                      slotProps={{
                                        htmlInput: {
                                          autoComplete: 'off',
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    width="120px"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography sx={{ fontWeight: 500 }}>
                                      {t('.datanomik.com')}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12}>
                                <Grid container>
                                  <Grid item xs={12}>
                                    <Button
                                      fullWidth
                                      type="submit"
                                      variant="contained"
                                    >
                                      {t('Continue')}
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12} textAlign="left">
                                    <Link
                                      onClick={() => {
                                        swiperRef.current?.slideTo(0, 500);
                                      }}
                                    >
                                      <ArrowLeftSLine />
                                      {t('Back')}
                                    </Link>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </form>
                        </FormProvider>
                      </Box>
                    </SwiperSlide>
                  </Swiper>
                </Grid>

              </Grid>
            </Box>
          </Grid>
          <Grid className="dn-show-mobile" item xs={12}>
            <Typography className="dn-legal-mobile" variant="body2">{t('© {{year}} Datanomik. All rights reserved.', { year })}</Typography>
          </Grid>
        </Grid>
      </Box>
      {
        openForgotPassword && (
          <ForgotPassword
            openModal={openForgotPassword}
            closeModal={handleCloseForgotPassword}
          />
        )
      }
    </Box>
  );
};

LogInRaw.propTypes = {
  className: PropTypes.string,
};

export const LogIn = styled(LogInRaw)(styles);

LogIn.propTypes = LogInRaw.propTypes;
